import { i18n } from '@lingui/core'
import { t } from '@lingui/core/macro'
import type {
  Citation,
  DatapointValueCitation,
  ResponseSegment,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'

export function collectDatapointValueCitations(
  citations: Citation[]
): DatapointValueCitation[] {
  const byEntryId = new Map<string, DatapointValueCitation>()
  for (const citation of citations) {
    if (citation.type !== 'datapoint_value') continue
    if (byEntryId.has(citation.entry_id)) continue
    byEntryId.set(citation.entry_id, citation)
  }
  return [...byEntryId.values()]
}

export function datapointValueCitationKey(
  citation: DatapointValueCitation
): string {
  return [
    citation.entry_id,
    citation.value ?? '',
    citation.timestamp ?? '',
    citation.aggregation_op ?? '',
  ].join('|')
}

/**
 * Per segment, the value-citation keys already shown by the segment directly
 * above it. Suppression is adjacent-only: a value re-cited later in a long
 * answer still gets its own chip.
 */
export function adjacentDuplicateValueCitationKeys(
  segments: readonly Pick<ResponseSegment, 'citations'>[]
): Set<string>[] {
  let previousKeys = new Set<string>()
  return segments.map((segment) => {
    const keys = new Set(
      collectDatapointValueCitations(segment.citations).map(
        datapointValueCitationKey
      )
    )
    const duplicates = new Set([...keys].filter((key) => previousKeys.has(key)))
    previousKeys = keys
    return duplicates
  })
}

export function formatDatapointValue(
  value: number | null | undefined,
  units?: string | null
): string {
  if (value === null || value === undefined) return t`no data`
  const magnitude = Math.abs(value)
  const maximumFractionDigits = magnitude >= 100 ? 1 : magnitude >= 1 ? 2 : 3
  const formatted = i18n.number(value, { maximumFractionDigits })
  return units ? `${formatted} ${units}` : formatted
}

export function formatCitationTimestamp(
  timestamp: string | null | undefined
): string | undefined {
  if (!timestamp) return undefined
  const parsed = new Date(timestamp)
  if (Number.isNaN(parsed.getTime())) return undefined
  return i18n.date(parsed, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function datapointCitationLabel(
  citation: DatapointValueCitation
): string {
  return citation.pi_tag ?? citation.name ?? citation.entry_id
}
